/**
 * Console-backed `Logger` for the v2 executor. No-ops when
 * `window.__debugRouteOps` and `REACT_APP_DEBUG_ROUTE_OPS` are both off, so
 * production builds keep the lines silent. When on, emits `[RouteOps]`-tagged
 * lines that match the v1 debugger's filter token, with structured payloads
 * (intent kind, strategy, phase, ms, error kind) for grep / DevTools filter.
 */
import { isDebugEnabled } from '../debug'
import type { Logger } from 'types/modules/routing'

/* eslint-disable no-console */

const TAG = '[RouteOps]'

const stamp = (): string => {
  const d = new Date()
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  const ms = String(d.getMilliseconds()).padStart(3, '0')
  return `${hh}:${mm}:${ss}.${ms}`
}

const NOOP: Logger = {
  debug: () => undefined,
  warn: () => undefined,
  error: () => undefined,
}

/**
 * Returns a `Logger` that emits to `console.{log,warn,error}` when debug is
 * on, otherwise a no-op. Live-evaluates the flag on every call so toggling
 * `window.__debugRouteOps` mid-session takes effect without a reload.
 */
export const createConsoleLogger = (): Logger => ({
  debug: (msg, payload) => {
    if (!isDebugEnabled()) return
    if (payload === undefined) console.log(`${TAG} ${stamp()} ${msg}`)
    else console.log(`${TAG} ${stamp()} ${msg}`, payload)
  },
  warn: (msg, payload) => {
    if (!isDebugEnabled()) return
    if (payload === undefined) console.warn(`${TAG} ${stamp()} ${msg}`)
    else console.warn(`${TAG} ${stamp()} ${msg}`, payload)
  },
  error: (msg, payload) => {
    if (!isDebugEnabled()) return
    if (payload === undefined) console.error(`${TAG} ${stamp()} ${msg}`)
    else console.error(`${TAG} ${stamp()} ${msg}`, payload)
  },
})

export const NOOP_LOGGER: Logger = NOOP
